import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
}

const BASE_URL = "https://sotsekadvocacia.com.br";

export function SEOHead({ title, description, canonicalPath, ogImage = "/og-image.png" }: SEOHeadProps) {
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper function to update or create a meta/link tag
    const updateTag = (selector: string, attribute: string, value: string, tagName: "meta" | "link" = "meta") => {
      let element = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
      
      if (!element) {
        element = document.createElement(tagName);
        if (tagName === "meta") {
          const attr = selector.includes("property=") ? "property" : "name";
          const attrValue = selector.match(/(?:name|property)="([^"]+)"/)?.[1];
          if (attrValue) {
            (element as HTMLMetaElement).setAttribute(attr, attrValue);
          }
        } else if (tagName === "link") {
          const relValue = selector.match(/rel="([^"]+)"/)?.[1];
          if (relValue) {
            (element as HTMLLinkElement).rel = relValue;
          }
        }
        document.head.appendChild(element);
      }
      
      element.setAttribute(attribute, value);
    };

    // Canonical URL - Critical for fixing duplicate content issues
    updateTag('link[rel="canonical"]', "href", canonicalUrl, "link");

    // Basic meta tags
    updateTag('meta[name="description"]', "content", description);

    // Open Graph tags
    updateTag('meta[property="og:title"]', "content", title);
    updateTag('meta[property="og:description"]', "content", description);
    updateTag('meta[property="og:url"]', "content", canonicalUrl);
    updateTag('meta[property="og:image"]', "content", fullOgImage);
    updateTag('meta[property="og:type"]', "content", "website");
    updateTag('meta[property="og:site_name"]', "content", "Sotsek Advocacia");

    // Twitter Card tags
    updateTag('meta[name="twitter:title"]', "content", title);
    updateTag('meta[name="twitter:description"]', "content", description);
    updateTag('meta[name="twitter:url"]', "content", canonicalUrl);
    updateTag('meta[name="twitter:image"]', "content", fullOgImage);

    // Cleanup function - restore original values when component unmounts
    return () => {
      // We don't remove the tags, just let the next page update them
    };
  }, [title, description, canonicalUrl, fullOgImage]);

  return null;
}
